import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useContact } from "../core/context/ContactContext";
import { usePageMeta } from "../hooks/usePageMeta";

import { serviceEditorial } from "./serviceEditorial";

const IMAGE_ROOT = "/images/gallery/";
const EMAIL = "info@arctusoft.tech";
const Arrow = ({ diagonal = false }: { diagonal?: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d={diagonal ? "M6 18 18 6M6 6h12v12" : "M4 12h15m-6-6 6 6-6 6"}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function Brand({ large = false }: { large?: boolean }) {
  return (
    <span className={`brand ${large ? "brand-large" : ""}`}>
      <img
        className="brand-mark"
        src={`${IMAGE_ROOT}arctusoft-mark-green.webp`}
        width="256"
        height="235"
        alt=""
        aria-hidden="true"
      />
      <span>arctusoft</span>
    </span>
  );
}

function SectionLabel({
  children,
  light = false,
}: {
  children: React.ReactNode;
  light?: boolean;
}) {
  return (
    <p className={`section-label ${light ? "label-light" : ""}`}>
      {children}
      <span aria-hidden="true" />
    </p>
  );
}

function Reveal({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || !("IntersectionObserver" in window)) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (media.matches) return;
    node.classList.add("reveal-ready");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add("revealed");
          observer.disconnect();
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(node);
    const showOnPreference = () => {
      if (media.matches) {
        node.classList.add("revealed");
        observer.disconnect();
      }
    };
    media.addEventListener("change", showOnPreference);
    return () => {
      observer.disconnect();
      media.removeEventListener("change", showOnPreference);
    };
  }, []);
  return (
    <div
      ref={ref}
      className={`reveal ${className}`}
      style={{ "--delay": `${delay}ms` } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

function Button({
  children,
  to = "/#contact",
  secondary = false,
  className = "",
  onClick,
}: {
  children: React.ReactNode;
  to?: string;
  secondary?: boolean;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <Link
      className={`gallery-button ${secondary ? "button-secondary" : ""} ${className}`}
      to={to}
      onClick={onClick}
    >
      <span>{children}</span>
      <Arrow />
    </Link>
  );
}

function useDepth(ref: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    const update = () => {
      frame = 0;
      if (media.matches) {
        node.style.setProperty("--depth", "0px");
        return;
      }
      const rect = node.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        node.style.setProperty(
          "--depth",
          `${Math.max(-22, Math.min(22, (window.innerHeight / 2 - rect.top - rect.height / 2) * 0.045))}px`,
        );
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", schedule, { passive: true });
    media.addEventListener("change", update);
    update();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      media.removeEventListener("change", update);
    };
  }, [ref]);
}

function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname, hash } = useLocation();
  const toggle = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const surfaces = [
      document.getElementById("main"),
      document.querySelector("footer"),
    ];
    surfaces.forEach((node) => node?.setAttribute("inert", ""));
    const desktop = window.matchMedia("(min-width: 601px)");
    const onResize = () => {
      if (desktop.matches) setOpen(false);
    };
    desktop.addEventListener("change", onResize);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggle.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
      surfaces.forEach((node) => node?.removeAttribute("inert"));
      desktop.removeEventListener("change", onResize);
    };
  }, [open]);
  const items = [
    ["Work", "work"],
    ["Services", "services"],
    ["Studio", "studio"],
  ];
  return (
    <header
      className={`site-header ${scrolled ? "header-scrolled" : ""} ${open ? "menu-is-open" : ""}`}
    >
      <div className="header-inner">
        <Link
          to="/#home"
          className="brand-link"
          aria-label="Arctusoft home"
          onClick={() => setOpen(false)}
        >
          <Brand />
        </Link>
        <nav className="desktop-nav" aria-label="Main navigation">
          {items.map(([label, id]) => (
            <Link
              key={id}
              className="nav-link"
              to={`/#${id}`}
              aria-current={
                pathname === "/" && hash === `#${id}` ? "location" : undefined
              }
            >
              {label}
            </Link>
          ))}
        </nav>
        <Button className="header-cta">Start a project</Button>
        <button
          ref={toggle}
          className="menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen(!open)}
        >
          <span />
          <span />
        </button>
      </div>
      <nav
        id="mobile-nav"
        className="mobile-nav"
        aria-label="Mobile navigation"
        hidden={!open}
      >
        {items.map(([label, id], i) => (
          <Link key={id} to={`/#${id}`} onClick={() => setOpen(false)}>
            <small>0{i + 1}</small>
            {label}
            <Arrow diagonal />
          </Link>
        ))}
        <Button onClick={() => setOpen(false)}>Start a project</Button>
        <a className="mobile-email" href={`mailto:${EMAIL}`}>
          {EMAIL}
        </a>
      </nav>
    </header>
  );
}

function RoutePosition() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
      return;
    }
    const frame = requestAnimationFrame(() => {
      const id = decodeURIComponent(hash.slice(1));
      document.getElementById(id)?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);
  useEffect(() => {
    const canonical = document.querySelector('link[rel="canonical"]');
    canonical?.setAttribute("href", `https://arctusoft.tech${pathname}`);
    document
      .querySelector('meta[property="og:url"]')
      ?.setAttribute("content", `https://arctusoft.tech${pathname}`);
  }, [pathname]);
  return null;
}

export function GalleryLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="gallery-site">
      <RoutePosition />
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Navigation />
      <main id="main">{children}</main>
      <Footer />
    </div>
  );
}

function Hero() {
  const image = useRef<HTMLDivElement>(null);
  useDepth(image);
  return (
    <section className="hero" id="home">
      <div className="hero-inner shell">
        <div className="hero-copy">
          <div className="hero-enter hero-meta">
            <SectionLabel>Ideas. Engineering. Impact.</SectionLabel>
          </div>
          <h1 aria-label="Exceptional software. Beautifully built.">
            <span className="headline-line">
              <span>Exceptional</span>
            </span>
            <span className="headline-line">
              <span>software.</span>
            </span>
            <span className="headline-line">
              <em>Beautifully</em>
              <span> built.</span>
            </span>
          </h1>
          <p className="hero-enter hero-description">
            Custom software, web platforms and mobile apps.
            <br className="desktop-break" /> Designed with intention. Engineered
            to scale.
          </p>
          <div className="hero-enter hero-actions">
            <Button>Start a project</Button>
            <Link to="/#work" className="text-link explore-link">
              <span className="play-circle">
                <Arrow diagonal />
              </span>
              Explore our work
            </Link>
          </div>
          <div className="hero-enter hero-location">
            <span className="availability-dot" />
            Based in Kochi. Building worldwide.
          </div>
        </div>
        <div ref={image} className="hero-art">
          <img
            src={`${IMAGE_ROOT}hero.webp`}
            srcSet={`${IMAGE_ROOT}hero-800.webp 800w, ${IMAGE_ROOT}hero.webp 1536w`}
            sizes="(max-width: 800px) 100vw, 55vw"
            alt="PRISM, OmyEarth and WorkersApp displayed on phones arranged on pale sage stone plinths"
            width="1536"
            height="1024"
            fetchPriority="high"
          />
          <span className="hero-art-caption">
            Thoughtful interfaces. Powerful engineering.
          </span>
        </div>
      </div>
      <div className="hero-bottom shell">
        <span>Independent software & design studio</span>
        <a href="#work" className="scroll-link">
          Discover what we build <span>↓</span>
        </a>
        <span className="hero-bottom-right">
          Crafted with care, from the first pixel.
        </span>
      </div>
    </section>
  );
}

const projects = [
  {
    name: "PRISM",
    category: "Education platform",
    headline: "A smarter school.\nA simpler everyday.",
    description:
      "A connected web and mobile experience for school operations, teachers and everyday learning.",
    image: "prism",
    alt: "Regenerated PRISM school management dashboard on a laptop with its teacher app on a phone",
    tags: ["Web platform", "Mobile application", "Product design"],
    overview:
      "PRISM brings school management and the teacher experience into one connected product. From front-office enquiries to attendance and classroom workflows, the platform makes complex daily operations easier to navigate.",
    features: [
      "A central workspace for front-office operations",
      "Teacher mobile experience for daily school workflows",
      "Clear navigation across school management modules",
    ],
  },
  {
    name: "WorkersApp",
    category: "Services marketplace",
    headline: "Everyday services.\nOne simple experience.",
    description:
      "A mobile marketplace that brings home services and roadside assistance into one intuitive experience.",
    image: "workers",
    alt: "Regenerated WorkersApp home services marketplace and cleaning service screens on two phones",
    tags: ["Mobile application", "Marketplace", "UI / UX design"],
    overview:
      "WorkersApp connects everyday service needs with an approachable mobile experience. Home maintenance and roadside assistance sit together in a clearly organised marketplace, designed to make finding the right service feel effortless.",
    features: [
      "Home services and repairs in one place",
      "Dedicated roadside assistance categories",
      "A clear mobile browsing and service selection experience",
    ],
  },
];
type Project = (typeof projects)[number];

function ProjectDialog({
  project,
  close,
}: {
  project: Project | null;
  close: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const node = dialog.current;
    if (!project || !node) return;
    node.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      node.close();
      document.body.style.overflow = previous;
    };
  }, [project]);
  return (
    <dialog
      ref={dialog}
      className="project-dialog"
      aria-labelledby="project-title"
      onCancel={close}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      {project && (
        <>
          <button
            className="dialog-close"
            aria-label="Close project"
            onClick={close}
          >
            ×
          </button>
          <img
            src={`${IMAGE_ROOT}${project.image}.webp`}
            srcSet={`${IMAGE_ROOT}${project.image}-800.webp 800w, ${IMAGE_ROOT}${project.image}.webp 1536w`}
            sizes="(max-width: 800px) 100vw, 60vw"
            alt={project.alt}
            width="1536"
            height="1024"
          />
          <div className="dialog-content">
            <SectionLabel>{project.category}</SectionLabel>
            <h2 id="project-title">{project.name}</h2>
            <p>{project.overview}</p>
            <ul>
              {project.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <div className="project-tags">
              {project.tags.map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
            <Link className="gallery-button" to="/#contact" onClick={close}>
              <span>Build a product with us</span>
              <Arrow />
            </Link>
          </div>
        </>
      )}
    </dialog>
  );
}

function Work() {
  const [selected, setSelected] = useState<Project | null>(null);
  return (
    <section className="work-section section-space shell" id="work">
      <Reveal>
        <SectionLabel>Selected work</SectionLabel>
        <div className="section-intro">
          <h2>
            Real products.
            <br />
            <em>Meaningful impact.</em>
          </h2>
          <p>
            Software made for the way people work,
            <br className="desktop-break" /> learn and live. A few things we’ve
            built.
          </p>
          <span className="work-index">01 — 02</span>
        </div>
      </Reveal>
      <div className="project-list">
        {projects.map((project, i) => (
          <Reveal
            key={project.name}
            className={`project-row ${i % 2 ? "project-reverse" : ""}`}
          >
            <button
              className="project-image"
              onClick={() => setSelected(project)}
              aria-label={`Explore ${project.name}`}
            >
              <img
                src={`${IMAGE_ROOT}${project.image}.webp`}
                srcSet={`${IMAGE_ROOT}${project.image}-800.webp 800w, ${IMAGE_ROOT}${project.image}.webp 1536w`}
                sizes="(max-width: 800px) 100vw, 60vw"
                width="1536"
                height="1024"
                loading="lazy"
                alt={project.alt}
              />
              <span className="image-view">
                Explore project <Arrow diagonal />
              </span>
              <span className="image-number">0{i + 1}</span>
            </button>
            <div className="project-copy">
              <p className="project-category">
                <strong>{project.name}</strong>
                <span>— {project.category}</span>
              </p>
              <h3>
                {project.headline.split("\n").map((line) => (
                  <React.Fragment key={line}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </h3>
              <p className="project-description">{project.description}</p>
              <div className="project-tags">
                {project.tags.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
              <button
                className="text-link"
                onClick={() => setSelected(project)}
              >
                Explore project
                <Arrow />
              </button>
            </div>
          </Reveal>
        ))}
      </div>
      <ProjectDialog project={selected} close={() => setSelected(null)} />
    </section>
  );
}

const services = [
  {
    slug: "product-development",
    name: "Custom software",
    short:
      "Purpose-built platforms that turn complex business needs into beautifully simple experiences.",
    title: "Your ambition.\nBeautifully engineered.",
    lead: "From the first idea to a product people rely on. Strategy, design and software engineering, brought together by one thoughtful team.",
    deliverables: [
      "Product strategy & discovery",
      "UX research & interface design",
      "Frontend & backend engineering",
      "API development & integrations",
      "Quality assurance & launch",
      "Ongoing product evolution",
    ],
    note: "Software shaped around your business, your users and what comes next.",
  },
  {
    slug: "web-development",
    name: "Web platforms",
    short:
      "Fast, considered web experiences. From customer-facing products to the tools behind your business.",
    title: "Made for the web.\nBuilt for your world.",
    lead: "Responsive websites, SaaS platforms and business applications that balance beautiful interfaces with dependable engineering.",
    deliverables: [
      "Responsive web applications",
      "SaaS platforms & dashboards",
      "Design systems",
      "API & third-party integrations",
      "Performance & accessibility",
      "Content & administration tools",
    ],
    note: "A clear interface on the surface. Thoughtful architecture underneath.",
  },
  {
    slug: "app-development",
    name: "Mobile apps",
    short:
      "Intuitive iOS and Android applications that make every interaction feel effortless.",
    title: "In their hands.\nAhead of expectations.",
    lead: "Mobile products designed around real life. We connect considered interfaces with the engineering that makes them feel natural, responsive and reliable.",
    deliverables: [
      "iOS & Android applications",
      "Cross-platform development",
      "Mobile UX & prototyping",
      "Backend & API integration",
      "Notifications & offline workflows",
      "Testing & release support",
    ],
    note: "From a first interaction to a daily habit. Every detail has a purpose.",
  },
  {
    slug: "ai-transformation",
    name: "AI & automation",
    short:
      "Practical intelligence that simplifies workflows and gives your team room to do more.",
    title: "Less busywork.\nMore possibility.",
    lead: "We help you find where AI can make a meaningful difference, then build it into the way your business works.",
    deliverables: [
      "AI opportunity discovery",
      "Workflow automation",
      "AI-powered product features",
      "Conversational interfaces",
      "Knowledge & document tools",
      "Integration & evaluation",
    ],
    note: "Useful intelligence, grounded in a real business need.",
  },
  {
    slug: "cloud-solutions",
    name: "Cloud & infrastructure",
    short: "The dependable foundation behind every great digital product.",
    title: "Quietly powerful.\nReady to grow.",
    lead: "Cloud architecture, deployment and operations designed to support your product today and give it room to grow tomorrow.",
    deliverables: [
      "Cloud architecture & migration",
      "Deployment pipelines",
      "Monitoring & observability",
      "Infrastructure automation",
      "Performance optimisation",
      "Security & reliability",
    ],
    note: "Thoughtful infrastructure lets your team focus on the product.",
  },
];

function Services() {
  return (
    <section className="services-section shell section-space" id="services">
      <Reveal className="capabilities-intro">
        <div>
          <SectionLabel>What we do</SectionLabel>
          <h2>
            Software,
            <br />
            <em>crafted end to end.</em>
          </h2>
        </div>
        <p>
          From the first idea to the product people rely on. We bring design and
          engineering together to build what your business needs next.
        </p>
      </Reveal>
      <Reveal className="capabilities-collection">
        <Link className="capability-feature" to="/services/product-development">
          <div className="capability-feature-copy">
            <span className="capability-eyebrow">01 / OUR CORE PRACTICE</span>
            <h3>
              Custom software.
              <br />
              <em>Your ambition, built.</em>
            </h3>
            <p>{services[0].short}</p>
            <div className="capability-tags">
              <span>Strategy</span>
              <span>Design</span>
              <span>Development</span>
            </div>
            <span className="capability-discover">
              Discover the service <Arrow />
            </span>
          </div>
          <div className="software-composition" aria-hidden="true">
            <div className="software-caption">
              COMPLEXITY, BEAUTIFULLY SIMPLIFIED
            </div>
            <div className="software-window">
              <div className="software-toolbar">
                <span className="software-monogram">a.</span>
                <span>Your product workspace</span>
                <i />
              </div>
              <div className="software-body">
                <div className="software-sidebar">
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <div className="software-dashboard">
                  <span className="software-overline">A CLEARER PICTURE</span>
                  <strong>Everything, in harmony.</strong>
                  <div className="software-metrics">
                    <div>
                      <small>Designed for</small>
                      <b>People.</b>
                    </div>
                    <div>
                      <small>Built for</small>
                      <b>Possibility.</b>
                    </div>
                  </div>
                  <div className="software-chart">
                    {[32, 49, 43, 65, 60, 78, 88, 100].map((height, i) => (
                      <span key={i} style={{ height: `${height}%` }} />
                    ))}
                  </div>
                  <div className="software-baseline">
                    <span>One connected experience</span>
                    <span>↗</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="software-note">
              <span>↗</span> Shaped around your business.
            </div>
          </div>
        </Link>
        <div className="capability-cards">
          {services.slice(1, 4).map((service, i) => (
            <Link
              className="capability-card"
              key={service.slug}
              to={`/services/${service.slug}`}
            >
              <div className="capability-card-top">
                <span>0{i + 2}</span>
                <span
                  className={`capability-symbol symbol-${i}`}
                  aria-hidden="true"
                >
                  <i />
                  <i />
                  <i />
                </span>
                <Arrow diagonal />
              </div>
              <h3>{service.name}</h3>
              <p>{service.short}</p>
              <span className="capability-discover">
                Discover the service <Arrow />
              </span>
            </Link>
          ))}
        </div>
        <Link to="/services/cloud-solutions" className="capability-foundation">
          <span className="foundation-mark" aria-hidden="true">
            ↳
          </span>
          <div>
            <h3>Cloud & infrastructure</h3>
            <p>The dependable foundation. Built in from day one.</p>
          </div>
          <span className="foundation-link">
            Explore the foundation <Arrow diagonal />
          </span>
        </Link>
      </Reveal>
    </section>
  );
}

const phases = [
  {
    title: "Understand",
    subtitle: "The right questions come first.",
    description:
      "We get close to your goals, your users and the problem worth solving. Together, we define a clear direction for the product.",
    tags: ["Discovery", "Product strategy", "Scope & roadmap"],
  },
  {
    title: "Design",
    subtitle: "Make the complex feel effortless.",
    description:
      "We turn ideas into considered interfaces and interactive prototypes. You see how the product feels before we start building it.",
    tags: ["UX & UI design", "Prototyping", "Design systems"],
  },
  {
    title: "Engineer",
    subtitle: "Bring every detail to life.",
    description:
      "Designers and engineers work together to build your product, with regular demos and careful attention to performance and quality.",
    tags: ["Development", "Integrations", "Quality assurance"],
  },
  {
    title: "Evolve",
    subtitle: "The launch is a beginning.",
    description:
      "We help launch, listen to real feedback and refine what comes next. Your product grows with your users and your business.",
    tags: ["Deployment", "Monitoring", "Continuous improvement"],
  },
];

function Process() {
  const [active, setActive] = useState(0);
  return (
    <section className="process-section section-space" id="process">
      <div className="shell">
        <Reveal>
          <SectionLabel>Our approach</SectionLabel>
          <div className="section-intro">
            <h2>
              Good things take care.
              <br />
              <em>We give them plenty.</em>
            </h2>
            <p>
              A close partnership. A clear process.
              <br />
              Room to make something remarkable.
            </p>
          </div>
        </Reveal>
        <Reveal className="process-layout">
          <div className="process-steps">
            {phases.map((phase, i) => (
              <div
                key={phase.title}
                className={`process-step ${active === i ? "step-active" : ""}`}
              >
                <button
                  aria-expanded={active === i}
                  aria-controls={`phase-${i}`}
                  onClick={() => setActive(i)}
                >
                  <span>0{i + 1}</span>
                  <h3>{phase.title}</h3>
                  <span className="step-toggle">
                    {active === i ? "−" : "+"}
                  </span>
                </button>
                <div
                  id={`phase-${i}`}
                  className="step-content"
                  hidden={active !== i}
                >
                  <p>{phase.description}</p>
                  <div className="process-tags">
                    {phase.tags.map((t) => (
                      <span key={t}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            className={`process-blueprint blueprint-phase-${active}`}
            aria-hidden="true"
          >
            <div className="blueprint-heading">
              <span>THE PRODUCT, TAKING SHAPE</span>
              <span>0{active + 1} / 04</span>
            </div>
            <div className="blueprint-scene" key={active}>
              <span className="blueprint-stage-label">
                {
                  [
                    "A shared direction",
                    "A considered experience",
                    "A connected system",
                    "Room to grow",
                  ][active]
                }
              </span>
              {active === 0 ? (
                <div className="direction-map">
                  <span className="map-note">The ambition</span>
                  <div className="map-path" />
                  <span className="map-note map-note-main">
                    The people
                    <br />
                    <em>at the heart of it.</em>
                  </span>
                  <div className="map-path" />
                  <span className="map-note">The opportunity</span>
                </div>
              ) : active === 1 ? (
                <div className="interface-study">
                  <div className="study-browser">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="study-layout">
                    <div className="study-sidebar" />
                    <div className="study-content">
                      <div className="study-title" />
                      <div className="study-line" />
                      <div className="study-tiles">
                        <i />
                        <i />
                        <i />
                      </div>
                      <div className="study-block" />
                    </div>
                  </div>
                  <span className="study-annotation">
                    Form follows purpose. ↗
                  </span>
                </div>
              ) : active === 2 ? (
                <div className="architecture-map">
                  <span>Interface</span>
                  <i />
                  <div>
                    <span>Application</span>
                    <span>Integrations</span>
                  </div>
                  <i />
                  <span>Data & infrastructure</span>
                </div>
              ) : (
                <div className="evolution-study">
                  <div className="evolution-bars">
                    {[24, 33, 39, 35, 53, 62, 69, 85, 96].map((height, i) => (
                      <i key={i} style={{ height: `${height}%` }} />
                    ))}
                  </div>
                  <div className="evolution-labels">
                    <span>Launch</span>
                    <span>Learn</span>
                    <span>Refine ↗</span>
                  </div>
                  <span className="evolution-caption">
                    Better with every chapter.
                  </span>
                </div>
              )}
              <h3>{phases[active].subtitle}</h3>
            </div>
            <div className="blueprint-footer">
              <span>DESIGN × ENGINEERING</span>
              <div className="blueprint-progress">
                {phases.map((phase, i) => (
                  <span
                    className={i <= active ? "progress-complete" : ""}
                    key={phase.title}
                  />
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const studioPrinciples = [
  {
    name: "Design",
    heading: "Care in every detail.",
    description:
      "We make complex products feel natural. From the first screen to the smallest interaction, design gives every decision a purpose.",
    details: [
      "Clear user journeys",
      "Considered interfaces",
      "Consistent design systems",
    ],
    word: "Considered.",
  },
  {
    name: "Engineering",
    heading: "Beauty, built to work.",
    description:
      "What happens beneath the surface matters just as much. We build dependable software with thoughtful architecture and room to grow.",
    details: [
      "Thoughtful architecture",
      "Reliable foundations",
      "Performance that matters",
    ],
    word: "Crafted.",
  },
  {
    name: "Partnership",
    heading: "Your team, extended.",
    description:
      "Great work starts with a shared table. We keep you close to the process, exchange ideas openly and care about the outcome as much as you do.",
    details: [
      "Direct collaboration",
      "Regular product demos",
      "A shared sense of ownership",
    ],
    word: "Connected.",
  },
];

function Studio() {
  const [active, setActive] = useState(0);
  const principle = studioPrinciples[active];
  return (
    <section className="studio-section section-space shell" id="studio">
      <Reveal className="studio-intro">
        <div>
          <SectionLabel>The studio</SectionLabel>
          <h2>
            A shared table.
            <br />
            <em>A better product.</em>
          </h2>
        </div>
        <p>
          We’re Arctusoft. An independent software and design studio in Kochi,
          bringing thoughtful design and careful engineering to ambitious teams
          everywhere.
        </p>
      </Reveal>
      <Reveal className="studio-feature" delay={80}>
        <div className="studio-signature" aria-hidden="true">
          <div className="signature-meta">
            <span>ARCTUSOFT / STUDIO NOTES</span>
            <span>01 — 03</span>
          </div>
          <div className="signature-art">
            <span className="signature-ring" />
            <span className="signature-rule signature-rule-one" />
            <span className="signature-rule signature-rule-two" />
            <img
              src={`${IMAGE_ROOT}arctusoft-mark-green.webp`}
              alt=""
              width="256"
              height="235"
            />
          </div>
          <div className="signature-type" key={principle.word}>
            <span>{principle.word}</span>
            <em>By intention.</em>
          </div>
          <div className="signature-footer">
            <span>KOCHI ↗ EVERYWHERE</span>
            <span>DESIGN × ENGINEERING</span>
          </div>
        </div>
        <div className="studio-philosophy">
          <SectionLabel>How we think</SectionLabel>
          <div
            className="studio-tabs"
            role="tablist"
            aria-label="Studio principles"
            onKeyDown={(e) => {
              let next = active;
              if (e.key === "ArrowRight") next = (active + 1) % 3;
              else if (e.key === "ArrowLeft") next = (active + 2) % 3;
              else if (e.key === "Home") next = 0;
              else if (e.key === "End") next = 2;
              else return;
              e.preventDefault();
              setActive(next);
              e.currentTarget
                .querySelectorAll<HTMLButtonElement>("button")
                [next].focus();
            }}
          >
            {studioPrinciples.map((item, i) => (
              <button
                key={item.name}
                id={`studio-tab-${i}`}
                role="tab"
                type="button"
                aria-selected={active === i}
                aria-controls="studio-principle-panel"
                tabIndex={active === i ? 0 : -1}
                onClick={() => setActive(i)}
              >
                <span aria-hidden="true">0{i + 1}</span>
                {item.name}
              </button>
            ))}
          </div>
          <div
            id="studio-principle-panel"
            role="tabpanel"
            aria-labelledby={`studio-tab-${active}`}
            tabIndex={0}
          >
            <div className="principle-content" key={active}>
              <h3>{principle.heading}</h3>
              <p>{principle.description}</p>
              <ul>
                {principle.details.map((detail) => (
                  <li key={detail}>
                    <span aria-hidden="true">↗</span>
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <Link to="/#contact" className="text-link studio-invitation">
            Meet your next product partner
            <Arrow />
          </Link>
        </div>
      </Reveal>
      <Reveal className="studio-footnote">
        <span>
          A clear purpose. A considered experience. A well-built product.
        </span>
        <span className="availability-dot" />
        One team, from idea to launch.
      </Reveal>
    </section>
  );
}

function Contact() {
  const { submitContactForm, isSubmitting, submitError, resetSubmitStatus } =
    useContact();
  const [data, setData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [selected, setSelected] = useState("");
  const confirmation = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (sent) confirmation.current?.focus();
  }, [sent]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await submitContactForm({
      ...data,
      subject: selected
        ? `Project inquiry: ${selected}`
        : "New project inquiry",
    });
    if (ok) {
      setSent(true);
      setData({ name: "", email: "", subject: "", message: "" });
    }
  };
  return (
    <section className="contact-section" id="contact">
      <div className="contact-banner">
        <div className="shell">
          <Reveal className="contact-banner-inner">
            <div>
              <SectionLabel light>Let’s build together</SectionLabel>
              <h2>
                Build something
                <br />
                <em>exceptional.</em>
              </h2>
            </div>
            <p>
              A new idea. A complex challenge.
              <br />A product ready for its next chapter.
              <br />
              <span>We’d love to hear about it.</span>
            </p>
            <a
              href="#project-form"
              className="contact-round"
              aria-label="Tell us about your project"
            >
              <Arrow diagonal />
            </a>
          </Reveal>
        </div>
      </div>
      <div className="contact-body shell section-space" id="project-form">
        <Reveal className="contact-details">
          <SectionLabel>A conversation starts here</SectionLabel>
          <h3>
            Tell us what
            <br />
            <em>you have in mind.</em>
          </h3>
          <p>
            You bring the ambition.
            <br />
            We’ll bring the design and engineering.
          </p>
          <a className="contact-email text-link" href={`mailto:${EMAIL}`}>
            {EMAIL}
            <Arrow diagonal />
          </a>
          <a className="contact-phone" href="tel:+919746696329">
            +91 97466 96329
          </a>
          <div className="contact-next">
            <span className="contact-next-label">WHAT HAPPENS NEXT</span>
            <div>
              <span>01</span>
              <p>
                Share your idea
                <strong>A few sentences are a good start.</strong>
              </p>
            </div>
            <div>
              <span>02</span>
              <p>
                Start a conversation
                <strong>We’ll talk through your goals together.</strong>
              </p>
            </div>
            <div>
              <span>03</span>
              <p>
                Find the right direction
                <strong>A clear approach to the next steps.</strong>
              </p>
            </div>
          </div>
          <div className="contact-location">
            <span className="availability-dot" />
            Kochi, Kerala · Working worldwide
          </div>
          <div className="social-links">
            <a
              href="https://www.linkedin.com/company/arctusoft"
              target="_blank"
              rel="noreferrer"
            >
              LinkedIn
              <Arrow diagonal />
            </a>
            <a
              href="https://www.instagram.com/arctusoft"
              target="_blank"
              rel="noreferrer"
            >
              Instagram
              <Arrow diagonal />
            </a>
            <a
              href="https://wa.me/918078592242"
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp
              <Arrow diagonal />
            </a>
          </div>
        </Reveal>
        <Reveal className="contact-form-wrap" delay={100}>
          <div className="inquiry-card-header">
            <span>PROJECT INQUIRY</span>
            <span className="inquiry-status">
              <span className="availability-dot" />
              {sent ? "Message received" : "Let’s make it real"}
            </span>
          </div>
          {sent ? (
            <div className="form-success" role="status">
              <span className="success-check">✓</span>
              <h3 ref={confirmation} tabIndex={-1}>
                A great place to start.
              </h3>
              <p>
                Your message is with us. We’ll be in touch to talk about your
                project.
              </p>
              <button
                className="text-link"
                onClick={() => {
                  setSent(false);
                  setSelected("");
                  resetSubmitStatus();
                }}
              >
                Send another message
                <Arrow />
              </button>
            </div>
          ) : (
            <form
              className="project-form"
              onSubmit={submit}
              aria-busy={isSubmitting}
            >
              <div className="inquiry-card-intro">
                <h3>
                  A good idea
                  <br />
                  <em>starts here.</em>
                </h3>
                <p>Tell us a little about yours.</p>
              </div>
              <fieldset>
                <legend>
                  <span className="form-section-number" aria-hidden="true">
                    01
                  </span>
                  I’m interested in…
                </legend>
                <div className="interest-options">
                  {[
                    "Custom software",
                    "Web platform",
                    "Mobile app",
                    "AI & automation",
                    "Something else",
                  ].map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={selected === option}
                      className={selected === option ? "interest-selected" : ""}
                      onClick={() =>
                        setSelected(selected === option ? "" : option)
                      }
                    >
                      <span className="interest-tick" aria-hidden="true">
                        ✓
                      </span>
                      {option}
                    </button>
                  ))}
                </div>
              </fieldset>
              <p className="form-section-title">
                <span className="form-section-number" aria-hidden="true">
                  02
                </span>
                A little about you
              </p>
              <div className="form-row">
                <label className={data.name.trim() ? "field-complete" : ""}>
                  <span className="field-caption">
                    <span id="inquiry-name-label">Your name</span>
                    <span className="field-check" aria-hidden="true">
                      ✓
                    </span>
                  </span>
                  <input
                    name="name"
                    aria-labelledby="inquiry-name-label"
                    autoComplete="name"
                    required
                    maxLength={150}
                    placeholder="Alex Taylor"
                    value={data.name}
                    onChange={(e) => setData({ ...data, name: e.target.value })}
                  />
                </label>
                <label
                  className={
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)
                      ? "field-complete"
                      : ""
                  }
                >
                  <span className="field-caption">
                    <span id="inquiry-email-label">Email address</span>
                    <span className="field-check" aria-hidden="true">
                      ✓
                    </span>
                  </span>
                  <input
                    name="email"
                    aria-labelledby="inquiry-email-label"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    placeholder="alex@company.com"
                    value={data.email}
                    onChange={(e) =>
                      setData({ ...data, email: e.target.value })
                    }
                  />
                </label>
              </div>
              <p className="form-section-title">
                <span className="form-section-number" aria-hidden="true">
                  03
                </span>
                The idea
              </p>
              <label className={data.message.trim() ? "field-complete" : ""}>
                <span className="field-caption">
                  <span id="inquiry-message-label">
                    A little about your project
                  </span>
                  <span className="field-check" aria-hidden="true">
                    ✓
                  </span>
                </span>
                <textarea
                  name="message"
                  aria-labelledby="inquiry-message-label"
                  rows={4}
                  required
                  maxLength={5000}
                  placeholder="The idea, the challenge, or where you’d like to go…"
                  value={data.message}
                  onChange={(e) =>
                    setData({ ...data, message: e.target.value })
                  }
                />
              </label>
              {submitError && (
                <p className="form-error" role="alert">
                  {submitError} You can also email{" "}
                  <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
                </p>
              )}
              <div className="form-bottom">
                <p>
                  By sending, you agree that we may contact
                  <br />
                  you about your project.
                </p>
                <button
                  className="gallery-button"
                  disabled={isSubmitting}
                  type="submit"
                >
                  <span>{isSubmitting ? "Sending…" : "Send your inquiry"}</span>
                  {isSubmitting ? (
                    <span className="sending-spinner" aria-hidden="true" />
                  ) : (
                    <Arrow />
                  )}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer shell">
      <div className="footer-top">
        <Link to="/#home" aria-label="Arctusoft home">
          <Brand />
        </Link>
        <nav aria-label="Footer navigation">
          <Link to="/#work">Work</Link>
          <Link to="/#services">Services</Link>
          <Link to="/#studio">Studio</Link>
          <Link to="/#contact">Contact</Link>
        </nav>
        <a href="#main" className="back-top">
          Back to top ↑
        </a>
      </div>
      <div className="company-registration" aria-label="Company registration">
        <div className="company-legal-name">
          <p>Arctusoft Technologies LLP</p>
        </div>
        <div className="company-registration-number">
          <span>LLPIN</span>
          <strong>ADC-4126</strong>
        </div>
      </div>
      <div className="footer-bottom">
        <span>Ideas. Engineering. Impact.</span>
        <span>
          © {new Date().getFullYear()} Arctusoft Technologies LLP. Crafted with
          care.
        </span>
      </div>
    </footer>
  );
}

export function GalleryHome() {
  usePageMeta(
    "Arctusoft — Exceptional software. Beautifully built.",
    "Design-led software development from Kochi to the world. Custom software, web platforms, mobile applications, AI and cloud solutions, crafted with care.",
  );
  return (
    <>
      <Hero />
      <Work />
      <Services />
      <Process />
      <Studio />
      <Contact />
    </>
  );
}

export function ServicePage() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  usePageMeta(
    service ? `${service.name} — Arctusoft` : "Page not found — Arctusoft",
    service?.lead || "Explore Arctusoft’s software development services.",
  );
  if (!service) return <NotFound />;
  const editorial = serviceEditorial[service.slug];
  return (
    <div className="service-editorial" key={service.slug}>
      <section className="service-hero service-hero-editorial shell">
        <Link className="text-link service-back" to="/#services">
          ← All services
        </Link>
        <div className="service-hero-composition">
          <Reveal className="service-hero-copy">
            <SectionLabel>{service.name}</SectionLabel>
            <h1>
              {service.title.split("\n").map((line, i) =>
                i ? (
                  <em key={line}>{line}</em>
                ) : (
                  <span key={line}>
                    {line}
                    <br />
                  </span>
                ),
              )}
            </h1>
            <p>{service.lead}</p>
            <Button>Let’s talk about your project</Button>
            <div className="service-scope">
              {editorial.scope.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </Reveal>
          <Reveal className="service-hero-art" delay={120}>
            <figure>
              <img
                src={`${IMAGE_ROOT}services/${service.slug}.webp`}
                srcSet={`${IMAGE_ROOT}services/${service.slug}-800.webp 800w, ${IMAGE_ROOT}services/${service.slug}.webp 1440w`}
                sizes="(max-width: 800px) 90vw, 48vw"
                alt={`Sculptural illustration for ${service.name.toLowerCase()}: ${editorial.caption}`}
                width="1440"
                height="960"
                fetchPriority="high"
              />
              <figcaption>
                <span>
                  ARCTUSOFT /{" "}
                  {String(services.indexOf(service) + 1).padStart(2, "0")}
                </span>
                <span>{editorial.caption}</span>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>
      <section className="service-intention shell">
        <Reveal className="service-intention-layout">
          <SectionLabel>The thinking behind it</SectionLabel>
          <p>{editorial.introduction}</p>
        </Reveal>
      </section>
      <section className="service-detail service-detail-editorial shell">
        <Reveal className="service-delivery-intro">
          <div>
            <SectionLabel>Built around you</SectionLabel>
            <h2>{service.note}</h2>
          </div>
          <p>
            A connected practice, from the first question to the smallest
            detail. Here’s what we can bring to your product.
          </p>
        </Reveal>
        <Reveal className="deliverables editorial-deliverables">
          {service.deliverables.map((item, i) => (
            <div key={item}>
              <span className="delivery-number">0{i + 1}</span>
              <h3>{item}</h3>
              <p>{editorial.details[i]}</p>
            </div>
          ))}
        </Reveal>
      </section>
      <section className="service-applications section-space">
        <div className="shell">
          <Reveal>
            <SectionLabel>Possibilities, with purpose</SectionLabel>
            <h2>
              Built for your
              <br />
              <em>next chapter.</em>
            </h2>
          </Reveal>
          <Reveal className="application-grid">
            {editorial.applications.map((item, i) => (
              <article key={item.title}>
                <span>0{i + 1} /</span>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </Reveal>
        </div>
      </section>
      <Process />
      <section className="service-questions shell section-space">
        <Reveal className="questions-layout">
          <div>
            <SectionLabel>A little clarity</SectionLabel>
            <h2>
              Good questions.
              <br />
              <em>Thoughtful answers.</em>
            </h2>
            <p>Every project is different. Let’s talk through yours.</p>
            <Link className="text-link" to="/#project-form">
              Start a conversation <Arrow />
            </Link>
          </div>
          <div className="questions-list">
            {editorial.faqs.map((item) => (
              <details key={item.question}>
                <summary>
                  {item.question}
                  <span className="question-toggle" aria-hidden="true" />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </Reveal>
      </section>
      <section className="related-services shell section-space">
        <Reveal>
          <SectionLabel>Connected capabilities</SectionLabel>
          <h2>Everything your product needs.</h2>
          <div className="related-grid editorial-related">
            {services
              .filter((s) => s.slug !== slug)
              .map((s) => (
                <Link to={`/services/${s.slug}`} key={s.slug}>
                  <div className="related-art">
                    <img
                      src={`${IMAGE_ROOT}services/${s.slug}-800.webp`}
                      alt=""
                      width="800"
                      height="533"
                      loading="lazy"
                    />
                  </div>
                  <div className="related-copy">
                    <h3>
                      {s.name}
                      <Arrow diagonal />
                    </h3>
                    <p>{s.short}</p>
                    <span>
                      Explore the service <Arrow />
                    </span>
                  </div>
                </Link>
              ))}
          </div>
        </Reveal>
      </section>
      <Contact />
    </div>
  );
}

export function NotFound() {
  return (
    <section className="not-found shell">
      <SectionLabel>404 — A little off course</SectionLabel>
      <h1>
        Let’s get you
        <br />
        <em>somewhere good.</em>
      </h1>
      <Button to="/">Back to the studio</Button>
    </section>
  );
}
